import React, { useState, useEffect } from 'react'
import Card from 'core-system/Card'
import {
  CheckCircle2,
  AlertCircle,
  Star,
  Download,
  Search,
  TrendingUp,
} from 'lucide-react'
import CountUp from 'react-countup'
import * as Tooltip from '@radix-ui/react-tooltip'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts'
import styled, { css } from 'styled-components'

// Simple className utility function
const cn = (...classes: (string | boolean | undefined)[]): string => {
  return classes.filter(Boolean).join(' ')
}

const MainContent = styled.main`
  flex: 1;
  overflow: auto;
`

const ContentContainer = styled.div`
  padding: 2rem;
`

const TitleSection = styled.div`
  margin-bottom: 2rem;
`

const PageTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`

const PageDescription = styled.p`
  font-size: 1rem;
  color: #4b5563;
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ChartCard = styled(Card)`
  padding: 1.5rem;
`

const ChartTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.5rem;
`

const SentimentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`

const PieChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const PieChartWrapper = styled.div`
  position: relative;
  width: 10rem;
  height: 10rem;
`

const PieChartCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PieChartCenterContent = styled.div`
  text-align: center;
`

const PieChartValue = styled.div`
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
`

const PieChartLabel = styled.div`
  font-size: 0.75rem;
  color: #4b5563;
`

const SentimentLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`

const SentimentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const SentimentDot = styled.div<{ $color: string }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  background-color: ${(props) => props.$color};
`

const SentimentText = styled.span`
  font-size: 0.75rem;
  color: #4b5563;
`

const TagsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;
`

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const Tag = styled.span`
  padding: 0.25rem 0.75rem;
  background-color: #f7edee;
  color: #a64d79;
  font-size: 0.75rem;
  border-radius: 9999px;
  font-weight: 500;
`

const InsightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`

const InsightItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`

const InsightContent = styled.div``

const InsightTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
`

const InsightDescription = styled.div`
  font-size: 0.75rem;
  color: #4b5563;
`

const TopicHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const FilterButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  border-radius: 9999px;
  transition: all 0.2s;
  border: none;
  cursor: pointer;

  ${(props) =>
    props.$active
      ? css`
          background-color: #a64d79;
          color: white;
        `
      : css`
          background-color: #f3f4f6;
          color: #4b5563;

          &:hover {
            background-color: #e5e7eb;
          }
        `}
`

const TopicList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const TopicItem = styled.div``

const TopicHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`

const TopicName = styled.span`
  font-size: 0.875rem;
  color: #374151;
`

const TopicValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
`

const ProgressBar = styled.div`
  height: 1.5rem;
  background-color: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
  margin-bottom: 0.25rem;
`

const ProgressFill = styled.div<{
  $width: string
  $color: string
  $delay?: string
}>`
  height: 100%;
  border-radius: 9999px;
  transition: all 0.2s;
  background-color: ${(props) => props.$color};
  width: ${(props) => props.$width};
`

const SparklineContainer = styled.div`
  height: 2rem;
  display: flex;
  align-items: center;
  gap: 0.125rem;
`

const SparklineBar = styled.div<{ $height: string; $delay?: string }>`
  flex: 1;
  background-color: #e5e7eb;
  border-radius: 0.125rem;
  height: ${(props) => props.$height};
`

const EffectivenessCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 2rem;
`

const EffectivenessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 2fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const StatsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const StatBox = styled.div`
  text-align: center;
  padding: 1.5rem;
  background-color: #f9fafb;
  border-radius: 0.5rem;
`

const StatValue = styled.div`
  font-size: 2.25rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
`

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
`

const ChartColumn = styled.div``

const ChartTitleSmall = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 1rem;
`

const LineChartContainer = styled.div`
  height: 16rem;
`

const FeedbackCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 2rem;
`

const FeedbackHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const FeedbackActions = styled.div`
  display: flex;
  gap: 0.5rem;
`

const SearchContainer = styled.div`
  position: relative;
`

const SearchIcon = styled(Search)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  color: #9ca3af;
`

const SearchInput = styled.input`
  padding-left: 2.25rem;
  padding-right: 1rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    outline: none;
    ring: 2px;
    ring-color: #a64d79;
    border-color: transparent;
  }
`

const SelectInput = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    outline: none;
    ring: 2px;
    ring-color: #a64d79;
    border-color: transparent;
  }
`

const OutlineButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #a64d79;
  color: #a64d79;
  background-color: transparent;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(166, 77, 121, 0.05);
  }
`

const TableContainer = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
`

const TableHead = styled.thead``

const TableRow = styled.tr<{ $hover?: boolean }>`
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.2s;

  ${(props) =>
    props.$hover &&
    css`
      &:hover {
        background-color: #f9fafb;
      }
    `}
`

const TableHeader = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
`

const TableBody = styled.tbody``

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #111827;
`

const TableCellSecondary = styled(TableCell)`
  color: #374151;
`

const SentimentIndicator = styled.div<{
  $sentiment: 'positive' | 'neutral' | 'negative'
}>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  background-color: ${(props) => {
    if (props.$sentiment === 'positive') return '#10b981'
    if (props.$sentiment === 'neutral') return '#eab308'
    return '#ef4444'
  }};
`

const CSATContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const SummaryCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 2rem;
  background-color: #f7edee;
  border: 1px solid #a64d79;
`

const SummaryContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`

const SummaryText = styled.div``

const SummaryTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`

const SummaryDescription = styled.p`
  font-size: 0.875rem;
  color: #374151;
`

const HighlightText = styled.span<{ $color: string }>`
  font-weight: 600;
  color: ${(props) => props.$color};
`

const CTAContainer = styled.div`
  display: flex;
  justify-content: center;
`

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #a64d79;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #7d0e22;
    transform: translateY(-0.125rem);
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
`

const Insights = React.memo(() => {
  const [isLoading, setIsLoading] = useState(true)
  const [sentimentFilter, setSentimentFilter] = useState('all')
  const [topicFilter, setTopicFilter] = useState('all')

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000)
    return () => clearTimeout(timer)
  }, [])

  const sentimentData = [
    { name: 'Positive', value: 72, color: '#10B981' },
    { name: 'Neutral', value: 21, color: '#F59E0B' },
    { name: 'Negative', value: 7, color: '#EF4444' },
  ]

  const topicData = [
    {
      name: 'Transit Enrollment',
      value: 847,
      color: '#A64D79',
      width: '100%',
      sparkline: [780, 790, 810, 820, 830, 840, 847],
    },
    {
      name: 'Parking',
      value: 623,
      color: '#B394C0',
      width: '74%',
      sparkline: [580, 590, 600, 610, 615, 620, 623],
    },
    {
      name: 'Pass Replacement',
      value: 412,
      color: '#C4A5D0',
      width: '49%',
      sparkline: [390, 395, 400, 405, 408, 410, 412],
    },
    {
      name: 'Stipend Questions',
      value: 312,
      color: '#D5B8E0',
      width: '37%',
      sparkline: [290, 295, 300, 305, 308, 310, 312],
    },
    {
      name: 'Micromobility',
      value: 218,
      color: '#E6CBF0',
      width: '26%',
      sparkline: [200, 205, 208, 210, 213, 215, 218],
    },
  ]

  const accuracyTrendData = [
    { week: 'W1', accuracy: 92 },
    { week: 'W2', accuracy: 93 },
    { week: 'W3', accuracy: 94 },
    { week: 'W4', accuracy: 95 },
    { week: 'W5', accuracy: 96 },
    { week: 'W6', accuracy: 96 },
    { week: 'W7', accuracy: 97 },
  ]

  const feedbackData = [
    {
      date: '2025-10-22',
      location: 'New York',
      topic: 'Transit Enrollment',
      sentiment: 'positive',
      csat: 5,
      escalated: false,
    },
    {
      date: '2025-10-22',
      location: 'San Francisco',
      topic: 'Parking',
      sentiment: 'neutral',
      csat: 4,
      escalated: false,
    },
    {
      date: '2025-10-22',
      location: 'Boston',
      topic: 'Pass Replacement',
      sentiment: 'positive',
      csat: 5,
      escalated: false,
    },
    {
      date: '2025-10-22',
      location: 'Chicago',
      topic: 'Stipend Questions',
      sentiment: 'negative',
      csat: 2,
      escalated: true,
    },
    {
      date: '2025-10-22',
      location: 'Denver',
      topic: 'Micromobility',
      sentiment: 'positive',
      csat: 5,
      escalated: false,
    },
  ]

  return (
    <Tooltip.Provider delayDuration={200}>
      <div>
        {/* Main Content */}
        <MainContent>
          <ContentContainer>
            <TitleSection>
              <PageTitle>AI Concierge Insights</PageTitle>
              <PageDescription>
                Understand what employees are asking, how they feel, and how AI
                performance evolves.
              </PageDescription>
            </TitleSection>

            {/* Sentiment & Employee Insights + Topic Breakdown */}
            <ChartsGrid>
              <ChartCard style={{ animationDelay: '0ms' }}>
                <ChartTitle>Sentiment & Employee Insights</ChartTitle>
                <SentimentGrid>
                  <PieChartContainer>
                    <PieChartWrapper>
                      <ResponsiveContainer width='100%' height='100%'>
                        <PieChart>
                          <Pie
                            data={sentimentData}
                            cx='50%'
                            cy='50%'
                            innerRadius={50}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey='value'
                            animationBegin={300}
                            animationDuration={1000}
                          >
                            {sentimentData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <PieChartCenter>
                        <PieChartCenterContent>
                          <PieChartValue>
                            <CountUp end={72} duration={1.8} suffix='%' />
                          </PieChartValue>
                          <PieChartLabel>Positive</PieChartLabel>
                        </PieChartCenterContent>
                      </PieChartCenter>
                    </PieChartWrapper>
                    <SentimentLegend>
                      {sentimentData.map((item) => (
                        <SentimentItem key={item.name}>
                          <SentimentDot $color={item.color} />
                          <SentimentText>
                            {item.name} ({item.value}%)
                          </SentimentText>
                        </SentimentItem>
                      ))}
                    </SentimentLegend>
                  </PieChartContainer>
                  <TagsContainer>
                    <TagsWrapper>
                      {[
                        'pre-tax',
                        'enrollment',
                        'lost card',
                        'stipend',
                        'parking',
                        'transit pass',
                      ].map((tag) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </TagsWrapper>
                  </TagsContainer>
                </SentimentGrid>
                <InsightsList>
                  <InsightItem>
                    <CheckCircle2
                      style={{
                        width: '1rem',
                        height: '1rem',
                        color: '#16a34a',
                        marginTop: '0.125rem',
                        flexShrink: 0,
                      }}
                    />
                    <InsightContent>
                      <InsightTitle>Top Positive Topic</InsightTitle>
                      <InsightDescription>
                        Transit allowance increases
                      </InsightDescription>
                    </InsightContent>
                  </InsightItem>
                  <InsightItem>
                    <AlertCircle
                      style={{
                        width: '1rem',
                        height: '1rem',
                        color: '#ca8a04',
                        marginTop: '0.125rem',
                        flexShrink: 0,
                      }}
                    />
                    <InsightContent>
                      <InsightTitle>Most Confusing Topic</InsightTitle>
                      <InsightDescription>
                        Parking reimbursement process
                      </InsightDescription>
                    </InsightContent>
                  </InsightItem>
                </InsightsList>
              </ChartCard>

              <ChartCard style={{ animationDelay: '100ms' }}>
                <TopicHeader>
                  <ChartTitle>Topic Breakdown</ChartTitle>
                  <FilterButtons>
                    <FilterButton
                      onClick={() => setTopicFilter('all')}
                      $active={topicFilter === 'all'}
                    >
                      By Location
                    </FilterButton>
                    <FilterButton
                      onClick={() => setTopicFilter('program')}
                      $active={topicFilter === 'program'}
                    >
                      By Program Type
                    </FilterButton>
                  </FilterButtons>
                </TopicHeader>
                <TopicList>
                  {topicData.map((topic, index) => (
                    <TopicItem key={topic.name}>
                      <TopicHeaderRow>
                        <TopicName>{topic.name}</TopicName>
                        <TopicValue>
                          {!isLoading && (
                            <CountUp
                              end={topic.value}
                              duration={1.8}
                              delay={0.1 + index * 0.1}
                            />
                          )}
                        </TopicValue>
                      </TopicHeaderRow>
                      <ProgressBar>
                        <ProgressFill
                          $width={isLoading ? '0%' : topic.width}
                          $color={topic.color}
                          $delay={`${100 + index * 100}ms`}
                        />
                      </ProgressBar>
                      <SparklineContainer>
                        {topic.sparkline.map((value, i) => {
                          const maxValue = Math.max(...topic.sparkline)
                          const height = `${(value / maxValue) * 100}%`
                          return (
                            <SparklineBar
                              key={i}
                              $height={height}
                              $delay={`${200 + index * 100 + i * 20}ms`}
                            />
                          )
                        })}
                      </SparklineContainer>
                    </TopicItem>
                  ))}
                </TopicList>
              </ChartCard>
            </ChartsGrid>

            {/* AI Effectiveness */}
            <EffectivenessCard style={{ animationDelay: '200ms' }}>
              <ChartTitle>AI Effectiveness</ChartTitle>
              <EffectivenessGrid>
                <StatsColumn>
                  <StatBox>
                    <StatValue>
                      <CountUp end={97} duration={1.8} suffix='%' />
                    </StatValue>
                    <StatLabel>Knowledge Accuracy</StatLabel>
                  </StatBox>
                  <StatBox>
                    <StatValue>
                      <CountUp end={91} duration={1.8} suffix='%' />
                    </StatValue>
                    <StatLabel>Escalation Accuracy</StatLabel>
                  </StatBox>
                </StatsColumn>
                <ChartColumn>
                  <ChartTitleSmall>Accuracy Trend (7 weeks)</ChartTitleSmall>
                  <LineChartContainer>
                    <ResponsiveContainer width='100%' height='100%'>
                      <LineChart data={accuracyTrendData}>
                        <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' />
                        <XAxis dataKey='week' stroke='#6B7280' fontSize={12} />
                        <YAxis
                          domain={[90, 100]}
                          stroke='#6B7280'
                          fontSize={12}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#1F2937',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#fff',
                          }}
                        />
                        <Line
                          type='monotone'
                          dataKey='accuracy'
                          stroke='#A64D79'
                          strokeWidth={3}
                          dot={{ fill: '#A64D79', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </LineChartContainer>
                </ChartColumn>
              </EffectivenessGrid>
            </EffectivenessCard>

            {/* Employee Feedback Log */}
            <FeedbackCard style={{ animationDelay: '300ms' }}>
              <FeedbackHeader>
                <ChartTitle>Employee Feedback Log</ChartTitle>
                <FeedbackActions>
                  <SearchContainer>
                    <SearchIcon />
                    <SearchInput type='text' placeholder='Search feedback...' />
                  </SearchContainer>
                  <SelectInput
                    value={sentimentFilter}
                    onChange={(e) => setSentimentFilter(e.target.value)}
                  >
                    <option value='all'>All Sentiment</option>
                    <option value='positive'>Positive</option>
                    <option value='neutral'>Neutral</option>
                    <option value='negative'>Negative</option>
                  </SelectInput>
                  <OutlineButton>Escalated Only</OutlineButton>
                </FeedbackActions>
              </FeedbackHeader>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Date</TableHeader>
                      <TableHeader>Location</TableHeader>
                      <TableHeader>Topic</TableHeader>
                      <TableHeader>Sentiment</TableHeader>
                      <TableHeader>CSAT</TableHeader>
                      <TableHeader>Escalated</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feedbackData.map((row, index) => (
                      <TableRow
                        key={index}
                        $hover
                        style={{ animationDelay: `${300 + index * 50}ms` }}
                      >
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.location}</TableCell>
                        <TableCellSecondary>{row.topic}</TableCellSecondary>
                        <TableCell>
                          <SentimentIndicator
                            $sentiment={
                              row.sentiment as
                                | 'positive'
                                | 'neutral'
                                | 'negative'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <CSATContainer>
                            <Star
                              style={{
                                width: '1rem',
                                height: '1rem',
                                color: '#fbbf24',
                                fill: '#fbbf24',
                              }}
                            />
                            <span>{row.csat}</span>
                          </CSATContainer>
                        </TableCell>
                        <TableCellSecondary>
                          {row.escalated ? 'Yes' : 'No'}
                        </TableCellSecondary>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </FeedbackCard>

            {/* Insights Summary Card */}
            <SummaryCard style={{ animationDelay: '400ms' }}>
              <SummaryContent>
                <TrendingUp
                  style={{
                    width: '1.5rem',
                    height: '1.5rem',
                    color: '#a64d79',
                    flexShrink: 0,
                    marginTop: '0.25rem',
                  }}
                />
                <SummaryText>
                  <SummaryTitle>Key Insights Summary</SummaryTitle>
                  <SummaryDescription>
                    Transit-related questions up{' '}
                    <HighlightText $color='#a64d79'>18% MoM</HighlightText>,
                    sentiment improved{' '}
                    <HighlightText $color='#16a34a'>+0.2 pts</HighlightText>;
                    most confusion around parking reimbursements.
                  </SummaryDescription>
                </SummaryText>
              </SummaryContent>
            </SummaryCard>

            {/* Export CTA */}
            <CTAContainer>
              <PrimaryButton>
                <Download style={{ width: '1rem', height: '1rem' }} />
                Download Insights Report →
              </PrimaryButton>
            </CTAContainer>
          </ContentContainer>
        </MainContent>
      </div>
    </Tooltip.Provider>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  Insights.displayName = 'Insights'
}

export default Insights
